import { createContext, useContext, useInsertionEffect, type ReactNode } from "react";
import { B, darken, type Tint } from "./tokens";

// The ENTIRE per-app surface of the design system. A product differs from
// every other product in exactly these three values — everything else in
// @b/ui is shared. A new app writes one of these in `src/brand.ts` and wraps
// its tree in <BrandProvider>.
export interface BrandConfig {
  /** Display name shown on the "Simple / [Name]" sub-brand pill, e.g. "Surveys". */
  name: string;
  /** X-Application slug / k8s namespace, e.g. "surveysoftware". */
  slug: string;
  /** The app's signature tint, e.g. B.tints.lavender. */
  tint: Tint;
  /** Optional square logo image (URL); replaces the built-in mark. */
  logo?: string;
}

const FALLBACK: BrandConfig = {
  name: "App",
  slug: "app",
  tint: B.tints.lavender,
};

const BrandContext = createContext<BrandConfig>(FALLBACK);

/** Read the active brand inside any b/ component or app page. */
export function useBrand(): BrandConfig {
  return useContext(BrandContext);
}

/**
 * Wrap the app root once (in main.tsx). Publishes the brand to context AND
 * injects the tint-derived CSS custom properties (--primary, --primary-dark,
 * --primary-focus) onto :root so raw HTML + base.css pick up the app's color
 * without any per-app CSS file. This is why base.css can stay app-agnostic.
 */
export function BrandProvider({ value, children }: { value: BrandConfig; children: ReactNode }) {
  useInsertionEffect(() => {
    const root = document.documentElement;
    const prev = {
      primary: root.style.getPropertyValue("--primary"),
      dark: root.style.getPropertyValue("--primary-dark"),
      focus: root.style.getPropertyValue("--primary-focus"),
    };
    root.style.setProperty("--primary", value.tint.ink);
    root.style.setProperty("--primary-dark", darken(value.tint.ink, 0.22));
    root.style.setProperty("--primary-focus", value.tint.ink + "26"); // ~15% alpha
    return () => {
      root.style.setProperty("--primary", prev.primary);
      root.style.setProperty("--primary-dark", prev.dark);
      root.style.setProperty("--primary-focus", prev.focus);
    };
  }, [value.tint.ink]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
